import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [siteInfo, setSiteInfo] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [siteRes, pageRes] = await Promise.all([
        axios.get(`${API_URL}/site-info`),
        axios.get(`${API_URL}/page-content/contact`)
      ]);
      setSiteInfo(siteRes.data);
      setPageContent(pageRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/contact`, formData);
      toast.success('Message sent! I\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-serif text-gray-900">{pageContent?.title || 'Get in Touch'}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent?.description || "I'm here to support you. Reach out with any questions or to schedule a consultation."}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-serif text-gray-900 mb-8">Send Me a Message</h2>
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone (Optional)
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="6"
                        className="w-full px-4 py-3 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors resize-none"
                        placeholder="Tell me how I can help you..."
                      ></textarea>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={submitting}
                      className="w-full bg-sage-700 hover:bg-sage-800 text-white py-6 text-lg rounded-xl transition-all duration-300 hover:shadow-lg"
                    >
                      <Send className="mr-2 h-5 w-5" />
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-serif text-gray-900 mb-8">Contact Information</h2>
                <div className="space-y-6">
                  <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="h-12 w-12 bg-sage-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-sage-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                        <a
                          href={`tel:${siteInfo?.phone || ''}`}
                          className="text-sage-700 hover:text-sage-800 transition-colors"
                        >
                          {siteInfo?.phone || 'Loading...'}
                        </a>
                        <p className="text-sm text-gray-600 mt-1">Available by appointment</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="h-12 w-12 bg-lavender-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-lavender-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                        <a
                          href={`mailto:${siteInfo?.email || ''}`}
                          className="text-sage-700 hover:text-sage-800 transition-colors break-all"
                        >
                          {siteInfo?.email || 'Loading...'}
                        </a>
                        <p className="text-sm text-gray-600 mt-1">I'll respond within 24 hours</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div className="h-12 w-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-rose-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Location</h3>
                        <p className="text-gray-700">{siteInfo?.location || 'Loading...'}</p>
                        <p className="text-sm text-gray-600 mt-1">Online sessions available worldwide</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <Card className="border-0 shadow-xl bg-gradient-to-br from-sage-700 to-sage-800 text-white">
                <CardContent className="p-8 text-center space-y-4">
                  <h3 className="text-2xl font-serif">Prefer WhatsApp?</h3>
                  <p className="text-sage-100">
                    Get quick responses and connect with me directly on WhatsApp
                  </p>
                  <a
                    href={siteInfo?.whatsapp || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-4 bg-white text-sage-800 rounded-xl hover:bg-sage-50 transition-all duration-300 hover:shadow-lg font-medium"
                  >
                    Chat on WhatsApp
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;